import { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { pdfUrl } from "../lib/api";
import { ArrowLeft, Plus, Trash2, Download, FileText, ListPlus, X, Users, Link2, Copy, CheckCircle2, XCircle, Sparkles, Camera, Mail, LayoutTemplate, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "../components/ClientPicker";
import { ClientField } from "../components/ClientField";
import EmailModal from "../components/EmailModal";
import MaterialListModal from "../components/MaterialListModal";
import { MaterialCalcModal } from "../components/MaterialCalcModal";
import { VatRatePicker } from "../components/VatRatePicker";
import { TuscaniaPickerTab } from "../components/TuscaniaPickerTab";
import { useAuth } from "../context/AuthContext";
import { isTech, isOffice } from "../lib/roles";
const n = (v) => Number(v) || 0;
const laborOf = (it) => (it.labor_price === undefined || it.labor_price === null || it.labor_price === "") && (it.material_price === undefined || it.material_price === null || it.material_price === "") ? n(it.unit_price) : n(it.labor_price);
const materialOf = (it) => n(it.material_price);
const priceOf = (it) => laborOf(it) + materialOf(it);
import { HistoryPanel } from "../components/audit/AuditList";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

const EMPTY = {
    title: "", client_id: null, client_name: "", client_address: "", site_address: "", notes: "",
    vat_rate: 21, valid_until: "", status: "koncept", items: [],
};

export default function BudgetEditor() {
    const { id } = useParams();
    const { user } = useAuth();
    const readOnly = isTech(user);
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const isNew = id === "novy";
    const [budget, setBudget] = useState(isNew ? EMPTY : null);
    const [catalog, setCatalog] = useState([]);
    const [tiles, setTiles] = useState([]);
    const [coatings, setCoatings] = useState([]);
    const [clients, setClients] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerTab, setPickerTab] = useState("catalog");
    const [pickerQ, setPickerQ] = useState("");
    const [clientPickerOpen, setClientPickerOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [materialOpen, setMaterialOpen] = useState(false);
    const [calcOpen, setCalcOpen] = useState(false);
    const [invoiceMenu, setInvoiceMenu] = useState(false);
    const [advancePct, setAdvancePct] = useState(50);
    const [company, setCompany] = useState(null);

    useEffect(() => {
        api.get("/catalog?sort=popular").then((r) => setCatalog(r.data)).catch(() => {});
        api.get("/tiles").then((r) => setTiles(r.data)).catch(() => {});
        api.get("/coatings").then((r) => setCoatings(r.data)).catch(() => {});
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
        api.get("/company").then((r) => setCompany(r.data)).catch(() => setCompany({}));
        if (!isNew) api.get(`/budgets/${id}`).then((r) => setBudget(r.data)).catch(() => navigate("/rozpocty"));
    }, [id, isNew, navigate]);

    const aiFromPhoto = async (file) => {
        if (!file) return;
        setAiBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/ai/budget-from-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (data.items && data.items.length > 0) {
                setBudget((b) => ({
                    ...b,
                    items: [...b.items, ...data.items.map((it) => ({
                        name: it.name, description: it.description, quantity: it.quantity || 1,
                        unit: it.unit || "ks", unit_price: it.unit_price || 0
                    }))]
                }));
                toast.success(`AI navrhla ${data.items.length} položek`);
                setAiOpen(false);
            } else {
                toast.error("AI nevrátila žádné položky.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI analýza selhala");
        } finally { setAiBusy(false); }
    };

    if (!budget) return <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání…</div>;

    const set = (k, v) => setBudget({ ...budget, [k]: v });

    const addBlank = () => setBudget({ ...budget, items: [...budget.items, { name: "", description: "", quantity: 1, unit: "ks", unit_price: 0, labor_price: 0, material_price: 0 }] });

    const addFromCatalog = (it) => {
        setBudget({
            ...budget,
            items: [...budget.items, { catalog_item_id: it.id, name: it.name, description: it.description, quantity: 1, unit: it.unit, unit_price: it.unit_price, min_amount: it.min_amount ?? null, labor_price: it.labor_price ?? it.unit_price, material_price: it.material_price ?? 0, craftsman_share_pct: it.craftsman_share_pct ?? null }],
        });
        setPickerOpen(false); setPickerQ("");
    };

    const addPickedItem = (row) => {
        setBudget({
            ...budget,
            items: [...budget.items, { name: row.name, description: row.description || "", quantity: 1, unit: row.unit, unit_price: row.price, labor_price: 0, material_price: row.price }],
        });
        setPickerOpen(false); setPickerQ("");
    };

    const updateItem = (idx, k, v) => {
        const items = [...budget.items];
        items[idx] = { ...items[idx], [k]: v };
        setBudget({ ...budget, items });
    };

    const delItem = (idx) => setBudget({ ...budget, items: budget.items.filter((_, i) => i !== idx) });

    const vatPayer = company ? company.vat_payer !== false : true;
    const lineTotal = (it) => Math.max(n(it.quantity) * priceOf(it), n(it.min_amount));
    const laborTotal = budget.items.reduce((s, it) => s + n(it.quantity) * laborOf(it) + Math.max(0, lineTotal(it) - n(it.quantity) * priceOf(it)), 0);
    const surcharges = budget.surcharges || [];
    const surchargeTotal = surcharges.reduce((s, x) => s + (x.enabled === false ? 0 : laborTotal * (n(x.pct) * (x.key === "floor_no_lift" ? Math.max(1, n(x.floors) || 1) : 1)) / 100), 0);
    const subtotal = budget.items.reduce((s, it) => s + lineTotal(it), 0) + surchargeTotal;
    const materialTotal = budget.items.reduce((s, it) => s + n(it.quantity) * materialOf(it), 0);
    const defPct = Number(company?.default_craftsman_share_pct ?? 51);
    const pctOf = (it) => (it.share_pct_is_default || it.craftsman_share_pct === null || it.craftsman_share_pct === undefined || it.craftsman_share_pct === "" ? defPct : n(it.craftsman_share_pct));
    const craftsmanTotal = budget.items.reduce((s, it) => s + n(it.quantity) * Math.round(laborOf(it) * pctOf(it)) / 100, 0);
    const office = isOffice(user);
    const vat = vatPayer ? subtotal * ((Number(budget.vat_rate) || 0) / 100) : 0;
    const total = subtotal + vat;

    const save = async () => {
        if (!budget.title) { toast.error("Vyplňte název"); return; }
        const payload = {
            title: budget.title, job_id: budget.job_id || null, client_id: budget.client_id || null,
            client_name: budget.client_name, client_address: budget.client_address,
            site_address: budget.site_address, notes: budget.notes,
            vat_rate: vatPayer ? (Number(budget.vat_rate) || 0) : 0, valid_until: budget.valid_until || null,
            status: budget.status || "koncept", surcharges: (budget.surcharges || []).map((x) => ({ key: x.key, label: x.label || "", pct: Number(x.pct) || 0, floors: x.floors ?? null, enabled: x.enabled !== false })),
            items: budget.items.map((it) => ({
                catalog_item_id: it.catalog_item_id || null, name: it.name, description: it.description, min_amount: it.min_amount ?? null,
                section: it.section || "", internal_note: it.internal_note || "", one_off: !!it.one_off, sort_order: it.sort_order ?? null,
                quantity: Number(it.quantity) || 0, unit: it.unit, unit_price: priceOf(it), labor_price: laborOf(it), material_price: materialOf(it),
                craftsman_share_pct: it.share_pct_is_default || it.craftsman_share_pct === "" || it.craftsman_share_pct === undefined || it.craftsman_share_pct === null ? null : Number(it.craftsman_share_pct),
            })),
            pdf_split_prices: !!budget.pdf_split_prices,
        };
        try {
            if (isNew) {
                const { data } = await api.post("/budgets", payload);
                toast.success("Rozpočet vytvořen");
                navigate(`/rozpocty/${data.id}`);
            } else {
                const { data } = await api.put(`/budgets/${id}`, payload);
                setBudget(data);
                toast.success("Uloženo");
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const del = async () => {
        if (!window.confirm("Smazat tento rozpočet?")) return;
        await api.delete(`/budgets/${id}`);
        navigate("/rozpocty");
    };

    const openPdf = () => {
        if (isNew) { toast.error("Nejdřív uložte rozpočet"); return; }
        window.open(pdfUrl(id), "_blank");
    };

    const createJob = async () => {
        if (!window.confirm("Vytvořit novou zakázku z tohoto rozpočtu? (klient, místo stavby a plánovaný příjem se přenesou)")) return;
        try { const { data } = await api.post(`/budgets/${id}/create-job`); toast.success("Zakázka vytvořena"); navigate(`/zakazky/${data.id}`); }
        catch (e) { toast.error(e.response?.data?.detail || "Nepodařilo se vytvořit zakázku"); }
    };
    const unlock = async () => {
        const reason = window.prompt("Důvod odemknutí odsouhlasené nabídky (zapíše se do Historie změn):");
        if (!reason) return;
        try { const { data } = await api.post(`/budgets/${id}/unlock`, { reason }); setBudget(data); toast.success("Rozpočet odemknut"); }
        catch (e) { toast.error(e.response?.data?.detail || "Odemknutí selhalo"); }
    };
    const createOffer = async () => {
        try { const { data } = await api.post("/offers", { budget_id: id }); toast.success(`Nabídka ${data.number} vytvořena (koncept)`); navigate(`/nabidky/${data.id}`); }
        catch (e) { toast.error(e.response?.data?.detail || "Vytvoření nabídky selhalo"); }
    };
    const makeInvoice = async (kind = "bezna") => {
        if (isNew) { toast.error("Nejdřív uložte rozpočet"); return; }
        try {
            const pct = Number(advancePct) || 50;
            const { data } = await api.post(`/invoices/from-budget/${id}?kind=${kind}&percent=${pct}`);
            toast.success(kind === "zalohova" ? `Zálohová faktura ${pct} % vytvořena` : kind === "konecna" ? "Konečná faktura vytvořena (zálohy odečteny)" : "Faktura vytvořena");
            setInvoiceMenu(false);
            navigate(`/faktury/${data.id}`);
        } catch { toast.error("Vytvoření faktury selhalo"); }
    };

    const saveAsTemplate = async () => {
        try {
            await api.post(`/budgets/${id}/duplicate?as_template=true`);
            toast.success("Uloženo jako šablona — najdete ji v Rozpočtech");
        } catch { toast.error("Uložení šablony selhalo"); }
    };

    const clientEmail = budget?.client_id ? (clients.find((c) => c.id === budget.client_id)?.email || "") : "";

    const filteredCatalog = catalog.filter((c) => !pickerQ || c.name.toLowerCase().includes(pickerQ.toLowerCase()));

    const tileRows = tiles.flatMap((t) => (t.variants || []).map((v) => ({
        key: `${t.id}-${v.id}`,
        name: `${t.name} — ${[v.size, v.color].filter(Boolean).join(", ")}`,
        description: t.manufacturer ? `Výrobce: ${t.manufacturer}` : "",
        sub: `${t.category === "obklad" ? "Obklad" : "Dlažba"}${t.manufacturer ? ` · ${t.manufacturer}` : ""}`,
        unit: v.unit || "m²",
        price: v.sell_price || 0,
    })));
    const filteredTiles = tileRows.filter((r) => !pickerQ || r.name.toLowerCase().includes(pickerQ.toLowerCase()));

    const coatingRows = coatings.map((c) => ({
        key: c.id,
        name: `Dekorativní stěrka — ${c.color}`,
        description: [c.manufacturer, c.effect].filter(Boolean).join(", "),
        sub: `${c.manufacturer || "—"}${c.effect ? ` · ${c.effect}` : ""} · práce + materiál`,
        unit: "m²",
        price: c.price_per_m2 || 0,
    }));
    const filteredCoatings = coatingRows.filter((r) => !pickerQ || r.name.toLowerCase().includes(pickerQ.toLowerCase()));

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="budget-editor-page">
            <Link to="/rozpocty" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-orange-600 mb-6">
                <ArrowLeft className="w-3 h-3" /> Zpět na rozpočty
            </Link>

            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">{isNew ? "Nový rozpočet" : `Rozpočet ${budget.number || ""}`}</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">{budget.title || "Nový rozpočet"}</h1>
                </div>
                {readOnly ? (
                    <div className="flex flex-wrap gap-2 items-center max-w-full">
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-slate-300 text-slate-600 rounded-sm" data-testid="budget-readonly-badge">Jen ke čtení</span>
                        {!isNew && <button onClick={openPdf} data-testid="export-pdf" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-sm inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF pro zákazníka</button>}
                    </div>
                ) : <div className="flex flex-wrap gap-2 max-w-full" data-testid="budget-header-actions">
                    {!isNew && (
                        <>
                            <button onClick={() => setMaterialOpen(true)} data-testid="material-list-button" title="Objednávka materiálu"
                                    className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1.5">
                                <ShoppingCart className="w-3.5 h-3.5" /> Materiál
                            </button>
                            <button onClick={() => setCalcOpen(true)} data-testid="material-calc-button" title="Výpočet materiálu podle norem + porovnání s nabídkou SIKO"
                                    className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1.5">
                                Výpočet materiálu
                            </button>
                            <button onClick={saveAsTemplate} data-testid="save-as-template" title="Uložit jako šablonu"
                                    className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1.5">
                                <LayoutTemplate className="w-3.5 h-3.5" /> Šablona
                            </button>
                            <button onClick={() => setEmailOpen(true)} data-testid="email-budget" title="Odeslat e-mailem"
                                    className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" /> E-mail
                            </button>
                            <div className="relative">
                                <button onClick={() => setInvoiceMenu(!invoiceMenu)} data-testid="make-invoice" className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" /> Vystavit fakturu
                                </button>
                                {invoiceMenu && (
                                    <div className="absolute right-0 mt-1 w-72 bg-white border-2 border-slate-900 shadow-lg z-30 p-2 space-y-1" data-testid="invoice-menu">
                                        <button onClick={() => makeInvoice("bezna")} data-testid="make-invoice-bezna" className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 rounded-sm">
                                            <div className="font-semibold">Běžná faktura</div>
                                            <div className="text-xs text-slate-500">Celá částka rozpočtu</div>
                                        </button>
                                        <div className="px-3 py-2 hover:bg-orange-50 rounded-sm">
                                            <div className="flex items-center justify-between gap-2">
                                                <div>
                                                    <div className="font-semibold text-sm">Zálohová faktura</div>
                                                    <div className="text-xs text-slate-500">Záloha předem</div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <input type="number" min="1" max="100" value={advancePct} onChange={(e) => setAdvancePct(e.target.value)} data-testid="advance-percent"
                                                           className="w-14 px-2 py-1 border border-slate-300 text-sm text-right rounded-sm" />
                                                    <span className="text-xs">%</span>
                                                </div>
                                            </div>
                                            <button onClick={() => makeInvoice("zalohova")} data-testid="make-invoice-zalohova" className="mt-2 w-full px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-sm">
                                                Vystavit zálohu {advancePct || 50} %
                                            </button>
                                        </div>
                                        <button onClick={() => makeInvoice("konecna")} data-testid="make-invoice-konecna" className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 rounded-sm">
                                            <div className="font-semibold">Konečná faktura</div>
                                            <div className="text-xs text-slate-500">Celý rozpočet − odečet vystavených záloh</div>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button onClick={openPdf} data-testid="export-pdf" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-sm inline-flex items-center gap-1.5">
                                <Download className="w-3.5 h-3.5" /> Stáhnout PDF
                            </button>
                            <button onClick={del} className="px-4 py-2 text-xs uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">Smazat</button>
                        </>
                    )}
                    <button onClick={save} disabled={!!budget.locked} data-testid="save-budget" className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                        {isNew ? "Vytvořit" : "Uložit"}
                    </button>
                </div>}
            </div>

            {budget.locked && (
                <div className="mb-4 border-2 border-slate-900 bg-slate-50 px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="budget-locked-banner">
                    <div className="text-sm"><span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-white mr-2">Odsouhlasená nabídka {budget.locked_by_offer}</span> Rozpočet je uzamčen. Změny ceny díla řešte přes Vícepráce / méněpráce.</div>
                    {user?.role === "owner" && <button onClick={unlock} className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-900 hover:bg-slate-900 hover:text-white rounded-sm" data-testid="budget-unlock">Odemknout (s důvodem)</button>}
                </div>
            )}
            <fieldset disabled={readOnly || !!budget.locked} className={`min-w-0 border-0 p-0 m-0 ${readOnly || budget.locked ? "budget-readonly" : ""}`}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <Card title="Rozpočet">
                    <Field label="Název rozpočtu *" v={budget.title} on={(e) => set("title", e.target.value)} testid="budget-title" />
                    <Field label="Platnost do" type="date" v={budget.valid_until || ""} on={(e) => set("valid_until", e.target.value)} />
                    <VatRatePicker value={budget.vat_rate} onChange={(v) => set("vat_rate", v)} vatPayer={vatPayer} testid="budget-vat" />
                    {!isNew && (
                        <div>
                            <label className="label-eyebrow block mb-1.5">Stav</label>
                            <select value={budget.status || "koncept"} onChange={(e) => set("status", e.target.value)} data-testid="budget-status"
                                    className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm">
                                <option value="koncept">Koncept</option>
                                <option value="odeslano">Odesláno klientovi</option>
                                <option value="schvaleno">Schváleno</option>
                                <option value="zamitnuto">Zamítnuto</option>
                            </select>
                        </div>
                    )}
                </Card>
                <Card title="Klient">
                    <ClientField label="Klient" value={budget.client_name} linked={!!budget.client_id} onOpen={() => setClientPickerOpen(true)} testid="budget-client" />
                    <Field label="Adresa klienta" v={budget.client_address} on={(e) => set("client_address", e.target.value)} />
                    <Field label="Místo stavby" v={budget.site_address} on={(e) => set("site_address", e.target.value)} />
                    <div>
                        <label className="label-eyebrow block mb-1.5">Zakázka</label>
                        <select value={budget.job_id || ""} onChange={(e) => set("job_id", e.target.value || null)} data-testid="budget-job" className="w-full min-w-0 min-h-[44px] px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white">
                            <option value="">— bez zakázky —</option>
                            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                        </select>
                        {budget.job_id && <Link to={`/zakazky/${budget.job_id}`} className="inline-block mt-1.5 text-[11px] text-orange-700 hover:underline" data-testid="budget-job-link">Otevřít zakázku →</Link>}
                        {!isNew && (office || !readOnly) && (
                            <div className="flex flex-wrap gap-2 mt-3" data-testid="budget-job-actions">
                                {office && <button onClick={createOffer} data-testid="budget-create-offer" className="flex-1 basis-[200px] max-w-full min-h-[44px] px-3 py-2 text-[10px] uppercase tracking-wider font-semibold bg-slate-900 text-white hover:bg-orange-600 rounded-sm whitespace-normal text-center leading-tight">Vytvořit nabídku (PDF)</button>}
                                {!budget.job_id && !readOnly && <button onClick={createJob} data-testid="budget-create-job" className="flex-1 basis-[200px] max-w-full min-h-[44px] px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-2 border-slate-900 hover:bg-slate-900 hover:text-white rounded-sm whitespace-normal text-center leading-tight">Vytvořit zakázku z rozpočtu</button>}
                            </div>
                        )}
                    </div>
                </Card>
                <Card title="Poznámka">
                    <textarea rows={4} value={budget.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="budget-notes"
                              className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm resize-none" />
                    <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!budget.pdf_split_prices} onChange={(e) => set("pdf_split_prices", e.target.checked)} data-testid="budget-split-toggle" className="w-4 h-4 accent-orange-600" /> V PDF a odkazu pro klienta rozdělit cenu na práci a materiál</label>
                </Card>
            </div>

            {/* Public link */}
            {!isNew && !readOnly && budget.public_token && (
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="public-link-section">
                    <div className="flex items-center gap-3 flex-1 min-w-[260px]">
                        <Link2 className="w-5 h-5 text-orange-400" />
                        <div>
                            <div className="label-eyebrow text-orange-400">Sdílení s klientem</div>
                            <div className="text-sm text-slate-200 mt-0.5">
                                {budget.status === "schvaleno" && <span className="inline-flex items-center gap-1 text-green-300"><CheckCircle2 className="w-4 h-4" /> Schváleno {budget.approved_by_name ? `– ${budget.approved_by_name}` : ""}</span>}
                                {budget.status === "zamitnuto" && <span className="inline-flex items-center gap-1 text-red-300"><XCircle className="w-4 h-4" /> Zamítnuto {budget.approved_by_name ? `– ${budget.approved_by_name}` : ""}</span>}
                                {budget.status !== "schvaleno" && budget.status !== "zamitnuto" && "Pošlete klientovi tento odkaz — schválí jedním kliknutím."}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-950/50 px-3 py-2 font-mono text-xs text-slate-300 flex-shrink min-w-0">
                        <span className="truncate" data-testid="public-link">{window.location.origin}/r/{budget.public_token}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/r/${budget.public_token}`); toast.success("Odkaz zkopírován"); }}
                                data-testid="copy-public-link"
                                className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                            <Copy className="w-3.5 h-3.5" /> Kopírovat
                        </button>
                        <a href={`/r/${budget.public_token}`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1.5 border border-slate-700 hover:bg-slate-800 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                            Otevřít
                        </a>
                    </div>
                </div>
            )}

            {/* Items */}
            <div className="bg-white border border-slate-200 mb-6">
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Položky rozpočtu</div>
                    {!readOnly && <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setAiOpen(true)} data-testid="ai-from-photo"
                                className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-sm inline-flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5" /> AI z fotky
                        </button>
                        <button onClick={() => setPickerOpen(true)} data-testid="pick-from-catalog"
                                className="px-3 py-1.5 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1">
                            <ListPlus className="w-3.5 h-3.5" /> Z ceníku
                        </button>
                        <button onClick={addBlank} data-testid="add-budget-item"
                                className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> Volná položka
                        </button>
                    </div>}
                </div>

                <div className="overflow-x-auto" data-testid="budget-items-scroll">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <th className="px-4 py-3 font-semibold w-8">#</th>
                            <th className="px-4 py-3 font-semibold">Položka</th>
                            <th className="px-4 py-3 font-semibold text-right w-24">Množství</th>
                            <th className="px-4 py-3 font-semibold w-16">MJ</th>
                            <th className="px-4 py-3 font-semibold text-right w-28">Práce/MJ</th>
                            <th className="px-4 py-3 font-semibold text-right w-28">Materiál/MJ</th>
                            <th className="px-4 py-3 font-semibold text-right w-28">Cena/MJ</th>
                            <th className="px-4 py-3 font-semibold text-right w-32">Celkem</th>
                            <th className="px-4 py-3 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="tabular-data">
                        {budget.items.length === 0 && (
                            <tr><td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                                Zatím žádné položky. Přidejte je z ceníku nebo ručně.
                            </td></tr>
                        )}
                        {budget.items.map((it, idx) => (
                            <Fragment key={idx}>
                            {it.section && it.section !== (budget.items[idx - 1]?.section || "") && (
                                <tr className="bg-slate-100 border-b border-slate-200"><td colSpan={9} className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-700" data-testid={`budget-section-${idx}`}>{it.section}</td></tr>
                            )}
                            <tr className="border-b border-slate-100">
                                <td className="px-4 py-2 text-center text-slate-400 font-mono text-xs">{idx + 1}</td>
                                <td className="px-4 py-2">
                                    <input value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Název položky"
                                           className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-orange-600 focus:outline-none focus:ring-0 text-sm rounded-sm font-medium" />
                                    {n(it.min_amount) > n(it.quantity) * priceOf(it) && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase border border-amber-400 bg-amber-50 text-amber-800" title={`Minimální částka ${fmt(n(it.min_amount))}`} data-testid={`item-min-${idx}`}>min.</span>}
                                    <input value={it.description || ""} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Popis (volitelné)"
                                           className="w-full px-2 py-0.5 border border-transparent hover:border-slate-200 focus:border-orange-600 focus:outline-none focus:ring-0 text-xs text-slate-500 rounded-sm" />
                                    {office && (it.internal_note || it.one_off) && (
                                        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm" data-testid={`item-internal-note-${idx}`}>
                                            {it.one_off && <span className="px-1 border border-sky-300 bg-sky-50 text-sky-800 text-[9px] uppercase font-bold shrink-0">jednorázová</span>}
                                            <span className="shrink-0 uppercase tracking-wider text-[9px] font-bold text-amber-700">interní</span>
                                            <input value={it.internal_note || ""} onChange={(e) => updateItem(idx, "internal_note", e.target.value)} placeholder="Interní poznámka (není v PDF pro klienta)"
                                                   className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 text-[11px] text-amber-900 p-0" />
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-2">
                                    <input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                                           className="w-full px-2 py-1 border border-slate-200 hover:border-slate-300 focus:border-orange-600 focus:outline-none text-sm rounded-sm text-right tabular-data" />
                                </td>
                                <td className="px-4 py-2">
                                    <input value={it.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)}
                                           className="w-full px-2 py-1 border border-slate-200 hover:border-slate-300 focus:border-orange-600 focus:outline-none text-sm rounded-sm" />
                                </td>
                                <td className="px-4 py-2">
                                    <input type="number" step="0.01" value={laborOf(it)} onChange={(e) => updateItem(idx, "labor_price", e.target.value)} data-testid={`item-labor-${idx}`}
                                           className="w-full px-2 py-1 border border-slate-200 hover:border-slate-300 focus:border-orange-600 focus:outline-none text-sm rounded-sm text-right tabular-data" />
                                    {office && <input type="number" step="0.5" value={it.share_pct_is_default ? "" : (it.craftsman_share_pct ?? "")} placeholder={`${defPct} %`} title="Podíl řemeslníka z ceny práce (%)" onChange={(e) => { const items = [...budget.items]; items[idx] = { ...items[idx], craftsman_share_pct: e.target.value, share_pct_is_default: e.target.value === "" }; setBudget({ ...budget, items }); }} data-testid={`item-pct-${idx}`}
                                           className="mt-1 w-full px-2 py-0.5 border border-dashed border-amber-300 text-[11px] text-amber-800 rounded-sm text-right tabular-data" />}
                                </td>
                                <td className="px-4 py-2">
                                    <input type="number" step="0.01" value={materialOf(it)} onChange={(e) => updateItem(idx, "material_price", e.target.value)} data-testid={`item-material-${idx}`}
                                           className="w-full px-2 py-1 border border-slate-200 hover:border-slate-300 focus:border-orange-600 focus:outline-none text-sm rounded-sm text-right tabular-data" />
                                </td>
                                <td className="px-4 py-2 text-right tabular-data" data-testid={`item-price-${idx}`}>{fmt(priceOf(it))}</td>
                                <td className="px-4 py-2 text-right font-semibold">{fmt(lineTotal(it))}</td>
                                <td className="px-4 py-2 text-right">
                                    {!readOnly && <button onClick={() => delItem(idx)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                                </td>
                            </tr>
                            </Fragment>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Totals */}
            {office && !isNew && (
                <div className="bg-white border border-slate-200" data-testid="budget-surcharges">
                    <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                        <span className="font-display font-bold uppercase tracking-wider text-sm">Přirážky <span className="text-xs text-slate-500 font-normal normal-case tracking-normal">(% z ceny práce, počítají se do podílu řemeslníka)</span></span>
                        <span className="text-sm font-semibold tabular-data" data-testid="budget-surcharges-sum">{fmt(surchargeTotal)}</span>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {[["weekend", "Víkendová práce", 100], ["floor_no_lift", "Patro bez výtahu (za každé patro)", 3], ["difficult", "Ztížené podmínky", 15], ["express", "Expresní termín", 20]].map(([k, l, d]) => {
                            const cur = surcharges.find((x) => x.key === k);
                            const defPctS = Number(company?.surcharge_defaults?.[k] ?? d);
                            const setS = (patch) => setBudget({ ...budget, surcharges: cur ? surcharges.map((x) => (x.key === k ? { ...x, ...patch } : x)) : [...surcharges, { key: k, label: l, pct: defPctS, floors: k === "floor_no_lift" ? 1 : null, enabled: true, ...patch }] });
                            return (
                                <label key={k} className={`border p-3 text-sm cursor-pointer ${cur?.enabled !== false && cur ? "border-orange-600 bg-orange-50/40" : "border-slate-200"}`} data-testid={`surcharge-${k}`}>
                                    <span className="flex items-center gap-2"><input type="checkbox" checked={!!cur && cur.enabled !== false} onChange={(e) => (cur ? setS({ enabled: e.target.checked }) : setS({}))} className="accent-orange-600" data-testid={`surcharge-${k}-toggle`} /> <span className="font-medium">{l}</span></span>
                                    <span className="flex items-center gap-2 mt-2 text-xs">
                                        <input type="number" step="0.5" min="0" value={cur?.pct ?? defPctS} onChange={(e) => setS({ pct: Number(e.target.value) })} className="w-20 px-2 py-1 border border-slate-300 rounded-sm" data-testid={`surcharge-${k}-pct`} /> %
                                        {k === "floor_no_lift" && <><span>×</span><input type="number" min="1" value={cur?.floors ?? 1} onChange={(e) => setS({ floors: Number(e.target.value) })} className="w-16 px-2 py-1 border border-slate-300 rounded-sm" data-testid="surcharge-floors" /> pater</>}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="flex justify-end">
                <div className="bg-white border border-slate-200 w-full md:w-96">
                    <Row label="Práce celkem" value={fmt(laborTotal)} testid="budget-labor-total" />
                    {surchargeTotal > 0 && <Row label="Přirážky" value={fmt(surchargeTotal)} testid="budget-surcharge-total" />}
                    <Row label="Materiál celkem" value={fmt(materialTotal)} testid="budget-material-total" />
                    {vatPayer ? (
                        <>
                            <Row label="Mezisoučet (bez DPH)" value={fmt(subtotal)} />
                            <Row label={`DPH ${budget.vat_rate}%`} value={fmt(vat)} />
                        </>
                    ) : (
                        <div className="px-6 py-2.5 border-b border-slate-100 text-xs text-slate-500" data-testid="budget-nonpayer-note">Nejsme plátci DPH.</div>
                    )}
                    <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
                        <span className="font-display font-bold text-sm uppercase tracking-wider">Celkem k úhradě</span>
                        <span className="font-display text-2xl font-black tabular-data" data-testid="budget-total">{fmt(total)}</span>
                    </div>
                    {office && (
                        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 text-xs space-y-1" data-testid="budget-internal-split">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Interní – podíly z ceny práce (zákazník ani technik nevidí)</div>
                            <div className="flex justify-between text-amber-900"><span>Podíl řemeslníků</span><b className="tabular-data" data-testid="budget-craftsman-total">{fmt(craftsmanTotal)}</b></div>
                            <div className="flex justify-between text-emerald-800"><span>Podíl firmy (práce − řemeslníci)</span><b className="tabular-data" data-testid="budget-company-total">{fmt(laborTotal - craftsmanTotal)}</b></div>
                        </div>
                    )}
                </div>
            </div>
            </fieldset>

            {/* Catalog picker modal */}
            {pickerOpen && (                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
                    <div className="bg-white border-2 border-slate-900 max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">Vyberte položku z ceníku</h2>
                            <button onClick={() => setPickerOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="flex border-b border-slate-200">
                            {[["catalog", "Ceník"], ["tiles", "Dlažba a obklady"], ["tuscania", "Tuscania"], ["coatings", "Stěrky"]].map(([k, label]) => (
                                <button key={k} onClick={() => setPickerTab(k)} data-testid={`picker-tab-${k}`}
                                        className={`px-5 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px ${pickerTab === k ? "border-orange-600 text-orange-700 bg-orange-50/50" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="p-4 border-b border-slate-200">
                            <input autoFocus placeholder="Hledat…" value={pickerQ} onChange={(e) => setPickerQ(e.target.value)}
                                   className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                        </div>
                        <div className="overflow-y-auto">
                            {pickerTab === "catalog" && (filteredCatalog.length === 0 ? (
                                <div className="p-8 text-center text-slate-500">
                                    Žádné položky v ceníku. <Link to="/cenik" className="text-orange-600 font-semibold">Přidat položky</Link>
                                </div>
                            ) : filteredCatalog.map((it) => (
                                <button key={it.id} onClick={() => addFromCatalog(it)}
                                        className="w-full flex items-center justify-between px-6 py-3 border-b border-slate-100 hover:bg-orange-50/50 text-left">
                                    <div>
                                        <div className="font-medium text-slate-900">{it.name}</div>
                                        <div className="text-xs text-slate-500">{it.category || "—"} · {it.unit}</div>
                                    </div>
                                    <div className="font-semibold tabular-data">{fmt(it.unit_price)}</div>
                                </button>
                            )))}
                            {pickerTab === "tiles" && (filteredTiles.length === 0 ? (
                                <div className="p-8 text-center text-slate-500">
                                    Žádné varianty. <Link to="/cenik-dlazby" className="text-orange-600 font-semibold">Přidat dlažby a obklady</Link>
                                </div>
                            ) : filteredTiles.map((r) => (
                                <button key={r.key} onClick={() => addPickedItem(r)} data-testid="picker-tile-row"
                                        className="w-full flex items-center justify-between px-6 py-3 border-b border-slate-100 hover:bg-orange-50/50 text-left">
                                    <div>
                                        <div className="font-medium text-slate-900">{r.name}</div>
                                        <div className="text-xs text-slate-500">{r.sub} · {r.unit}</div>
                                    </div>
                                    <div className="font-semibold tabular-data">{fmt(r.price)}</div>
                                </button>
                            )))}
                            {pickerTab === "coatings" && (filteredCoatings.length === 0 ? (
                                <div className="p-8 text-center text-slate-500">
                                    Žádné stěrky. <Link to="/cenik-sterky" className="text-orange-600 font-semibold">Přidat stěrky</Link>
                                </div>
                            ) : filteredCoatings.map((r) => (
                                <button key={r.key} onClick={() => addPickedItem(r)} data-testid="picker-coating-row"
                                        className="w-full flex items-center justify-between px-6 py-3 border-b border-slate-100 hover:bg-orange-50/50 text-left">
                                    <div>
                                        <div className="font-medium text-slate-900">{r.name}</div>
                                        <div className="text-xs text-slate-500">{r.sub}</div>
                                    </div>
                                    <div className="font-semibold tabular-data">{fmt(r.price)} / m²</div>
                                </button>
                            )))}
                            {pickerTab === "tuscania" && <TuscaniaPickerTab q={pickerQ} onAdd={(it) => { setBudget((b) => ({ ...b, items: [...b.items, it] })); setPickerOpen(false); setPickerQ(""); toast.success("Položka přidána"); }} />}
                        </div>
                    </div>
                </div>
            )}

            {!isNew && !readOnly && <div className="mt-6"><HistoryPanel entityId={id} /></div>}

            {materialOpen && !isNew && (
                <MaterialListModal budgetId={id} onClose={() => setMaterialOpen(false)} />
            )}
            {calcOpen && !isNew && <MaterialCalcModal budgetId={id} jobId={budget.job_id} onClose={() => setCalcOpen(false)} />}

            {emailOpen && !isNew && (
                <EmailModal docType="budget" docId={id} defaultTo={clientEmail}
                            defaultSubject={`Cenová nabídka ${budget.number || ""} — ${budget.title}`}
                            defaultBody={`Dobrý den,\n\nv příloze Vám zasíláme cenovou nabídku "${budget.title}".\n\nV případě dotazů nás neváhejte kontaktovat.\n\nS pozdravem`}
                            onClose={() => setEmailOpen(false)} />
            )}

            {clientPickerOpen && (
                <ClientPicker clients={clients} onPick={(c) => {
                    setBudget({
                        ...budget, client_id: c.id, client_name: c.name,
                        client_address: [c.address, [c.zip, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")
                    });
                    setClientPickerOpen(false);
                }} onClose={() => setClientPickerOpen(false)} />
            )}

            {aiOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !aiBusy && setAiOpen(false)}>
                    <div className="bg-white border-2 border-slate-900 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900 bg-gradient-to-r from-orange-50 to-orange-100">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-orange-600" />
                                <h2 className="font-display text-lg font-bold">AI návrh z fotky</h2>
                            </div>
                            {!aiBusy && <button onClick={() => setAiOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>}
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600">Nahrajte fotku stavebního prostoru. AI (Claude Sonnet) navrhne 3–10 položek do rozpočtu, které pak můžete upravit.</p>
                            {aiBusy ? (
                                <div className="text-center py-12">
                                    <Sparkles className="w-10 h-10 text-orange-600 mx-auto mb-3 animate-pulse" />
                                    <div className="text-sm font-semibold text-slate-700">Analyzuji fotku…</div>
                                    <div className="text-xs text-slate-500 mt-1">Může to trvat 10–30 sekund.</div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-3">
                                    <label className="flex items-center justify-center gap-2 w-full p-6 border-2 border-dashed border-slate-300 hover:border-orange-400 cursor-pointer rounded-sm">
                                        <FileText className="w-5 h-5 text-slate-400" />
                                        <span className="text-sm text-slate-600">Nahrát soubor (JPG/PNG)</span>
                                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="ai-file-input"
                                               onChange={(e) => e.target.files?.[0] && aiFromPhoto(e.target.files[0])} />
                                    </label>
                                    <label className="flex items-center justify-center gap-2 w-full p-6 border-2 border-dashed border-orange-400 bg-orange-50/30 hover:bg-orange-50 cursor-pointer rounded-sm">
                                        <Camera className="w-5 h-5 text-orange-600" />
                                        <span className="text-sm text-orange-800 font-semibold">Vyfotit teď</span>
                                        <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="ai-camera-input"
                                               onChange={(e) => e.target.files?.[0] && aiFromPhoto(e.target.files[0])} />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white border border-slate-200 sharp-shadow">
            <div className="px-6 py-3 border-b-2 border-slate-900">
                <div className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">{title}</div>
            </div>
            <div className="p-6 space-y-4">{children}</div>
        </div>
    );
}

function Field({ label, v, on, type = "text", testid }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v} onChange={on} data-testid={testid}
                   className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm" />
        </div>
    );
}

function Row({ label, value, testid }) {
    return (
        <div className="flex items-center justify-between px-6 py-2.5 border-b border-slate-100 text-sm" data-testid={testid}>
            <span className="text-slate-600">{label}</span>
            <span className="font-semibold tabular-data">{value}</span>
        </div>
    );
}
